import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  ArrowUpRight,
  ArrowDownRight,
  Users,
  MessageSquare,
  Eye,
  MousePointer,
  Sparkles,
  ThumbsUp,
  Send,
  BarChart,
  Percent,
  FileText,
  DollarSign,
  Hash,
  Loader2,
  Wand2,
  MinusCircle,
  NotebookPen,
  ChevronDown,
  Smile
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { enUS, fr, de, it as itLocale, es as esLocale, ptBR } from 'date-fns/locale';
import { SocialIcon, type SocialPlatform } from '../components/icons/SocialIcon';
import { DateRangePicker } from '../components/common/DateRangeSelector';
import { BrandDropdown } from '../components/common/BrandDropdown';
import { startOfMonth, endOfMonth, subMonths, startOfYear, format } from 'date-fns';
import { SocialMediaTabs } from '../components/common/SocialMediaTabs';
import { useAnalyticsSummary } from '../hooks/useAnalyticsSummary.ts'
import { usePlatformOverview } from '../hooks/usePlatformOverview.ts';
import { Brand } from '../types/analytics';
import { useAccounts } from '../hooks/useAccounts.ts';
import { LinkBrandRedirectionCard } from '../components/common/LinkBrandRedirectionCard';
import { useAnalyticsUrlState } from '../store/analyticsStore';
import { useSearchParams } from 'react-router-dom';
import { useBrands, type Brand as BrandsHookBrand, type BrandsResponse } from '../hooks/useBrands.ts';
import { useOnDemandAiAnalysis } from '../hooks/useOnDemandAiAnalysis.ts';
import type { AiAnalysisData } from '../services/analyticsService.ts';
import { PageLayout } from '../components/layout';
import TimeSeriesChart from '../components/analytics/TimeSeriesChart';
import { startOfWeek, parseISO, format as formatDate } from 'date-fns';
import { InfoTooltip } from '../components/common/InfoTooltip';

// Define a type for connected pages (adjust to your actual data structure)
interface ConnectedPage {
  platform: SocialPlatform | string;
  brandId: string;
  pageId?: string;
  name?: string; 
  // Add other properties from your actual page object if needed
}

// Define platform names for display
interface Platform {
  id: string;
  name: string;
}

// Only show supported platforms in tabs (remove Twitter, TikTok, Pinterest, Google Business)
const platforms: Platform[] = [
  { id: 'linkedin', name: 'LinkedIn' },
  { id: 'youtube', name: 'YouTube' },
  { id: 'instagram', name: 'Instagram' },
  { id: 'facebook', name: 'Facebook' }
];

// Define a type for our metrics
interface DisplayMetric {
  title: string;
  value: number;
  previousValue: number;
  change: number;
  icon: React.ElementType;
  color: string;
  rawValueChange?: string; // To display "∞" or the original percentage string
  isPercentage?: boolean; // If true, show value with % suffix
  description?: string;
  hideChange?: boolean;
  hidePrevious?: boolean;
}

// Interface for metrics coming from the backend's keyMetrics and secondaryMetrics
interface BackendMetricItem {
  type: string;
  total: number;
  previousTotal: number;
  variationsPercentage: string;
  description?: string;
}

// Mock data for fallback
const mockSummaryMetrics: DisplayMetric[] = [
  {
    title: 'Total Followers',
    value: 1823,
    previousValue: 1642,
    change: 11.02,
    icon: Users,
    color: 'bg-blue-500'
  },
  {
    title: 'Total Engagement',
    value: 3127,
    previousValue: 2819,
    change: 10.93,
    icon: ThumbsUp,
    color: 'bg-green-500'
  },
  {
    title: 'Total Posts',
    value: 58,
    previousValue: 62,
    change: -6.45,
    icon: FileText,
    color: 'bg-orange-500'
  },
  {
    title: 'Total Impressions',
    value: 27305,
    previousValue: 24112,
    change: 13.24,
    icon: Eye,
    color: 'bg-indigo-500'
  },
  {
    title: 'Avg. Engagement Rate',
    value: 6.93,
    previousValue: 5.72,
    change: 21.15,
    icon: Percent,
    color: 'bg-rose-500'
  }
];

// Mock AI insights for fallback
const mockAIInsights = {
  summary: "La performance globale sur toutes les plateformes s'est améliorée, avec une croissance notable des abonnés et de l'engagement. Bien que le nombre de publications ait légèrement diminué, l'efficacité du contenu s'est accrue significativement.",
  analysis: "L'augmentation de 29,59% des abonnés organiques indique une stratégie d'acquisition réussie, probablement due à un contenu plus engageant ou à une meilleure visibilité.",
  strengths: "Forte croissance des abonnés organiques, augmentation significative de l'engagement et des clics, amélioration du taux d'engagement et du CTR.",
  improvements: "Explorer les raisons de la diminution du nombre de publications et évaluer si cela affecte la croissance à long terme."
};

const Analytics = () => {
    // Time grouping state for charts (daily by default)
    const [timeGrouping, setTimeGrouping] = useState<'daily' | 'weekly'>('daily');

    // Helper: aggregate a time series to weekly sums (Monday week start)
    const aggregateToWeekly = useCallback((series: Array<{ date: string; values: Record<string, number> }>): Array<{ date: string; values: Record<string, number> }> => {
      if (!Array.isArray(series)) return [];
      const buckets = new Map<string, Record<string, number>>();
      for (const point of series) {
        if (!point || !point.date) continue;
        const d = parseISO(point.date);
        const weekStart = formatDate(startOfWeek(d, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        const acc = buckets.get(weekStart) || {};
        const values = point.values || {};
        for (const key of Object.keys(values)) {
          const val = Number(values[key] ?? 0);
          acc[key] = (acc[key] || 0) + (isFinite(val) ? val : 0);
        }
        buckets.set(weekStart, acc);
      }
      return Array.from(buckets.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, values]) => ({ date, values }));
    }, []);
  const { t, i18n } = useTranslation();
  const lastMonth = subMonths(new Date(), 1);
  const [searchParams] = useSearchParams();
  const refreshQueryParam = searchParams.get('refresh');
  const [currentAiData, setCurrentAiData] = useState<AiAnalysisData | null>(null);
  
  // Use URL state management instead of local state
  const {
    activeTab,
    setActiveTab,
    selectedBrand,
    setSelectedBrand,
    dateRange,
    setDateRange,
    customDateRange,
    setCustomDateRange
  } = useAnalyticsUrlState();
  
  const [selectedPlatform, setSelectedPlatform] = useState<SocialPlatform>('linkedin');

  // Fetch ALL brands for the dropdown
  const { data: allBrandsData, isLoading: isLoadingAllBrands } = useBrands();

  const processedAllBrands: Brand[] = useMemo(() => {
    const dataAsBrandsResponse = allBrandsData as (BrandsResponse | undefined);
    if (!dataAsBrandsResponse?.brands) return [];
    return dataAsBrandsResponse.brands.map((b: BrandsHookBrand) => ({
      id: b.id,
      name: b.name,
      logo: b.logo,
      website: b.website || '',
    }));
  }, [allBrandsData]);
  
  // Fetch accounts data to check platform availability
  const { data: accountsData, isLoading: isAccountsLoading } = useAccounts();
  const connectedPages: ConnectedPage[] = accountsData?.connected_pages || [];
  
  // Filter brands to only show those with connected accounts
  const brandsWithConnectedAccounts = useMemo(() => {
    if (!connectedPages.length) return processedAllBrands; // If no accounts, show all brands for connection
    
    // Get unique brand IDs from connected pages
    const brandIds = [...new Set(connectedPages.map(page => page.brandId).filter(Boolean))];
    
    // Filter brands to only include those with connected accounts
    return processedAllBrands.filter((brand: Brand) => brandIds.includes(brand.id));
  }, [processedAllBrands, connectedPages]);
  
  // Check if any accounts are connected
  const hasConnectedAccounts = connectedPages.length > 0;

  // When multiple brands exist and none is selected, block fetching and show a brand selection card
  const brandSelectionRequired = useMemo(() => {
    return !isLoadingAllBrands && !isAccountsLoading && (brandsWithConnectedAccounts.length > 1) && !selectedBrand;
  }, [isLoadingAllBrands, isAccountsLoading, brandsWithConnectedAccounts, selectedBrand]);

  // If there is exactly one brand with connected accounts, auto-select it to keep current behavior
  useEffect(() => {
    if (!isLoadingAllBrands && !isAccountsLoading && !selectedBrand && brandsWithConnectedAccounts.length === 1) {
      setSelectedBrand(brandsWithConnectedAccounts[0].id);
    }
  }, [isLoadingAllBrands, isAccountsLoading, brandsWithConnectedAccounts, selectedBrand, setSelectedBrand]);

  // Convert date range to string format for API
  const getFormattedDateRange = () => {
    if (dateRange === 'custom') {
      return {
        start: format(customDateRange.start, 'yyyy-MM-dd'),
        end: format(customDateRange.end, 'yyyy-MM-dd')
      };
    } else if (dateRange === 'this-month') {
      // Include today: users publishing today expect to see their content
      // appear immediately (top posts, video count). Analytics metrics that
      // lag 24-72h are still flagged by the per-platform disclaimer.
      const now = new Date();
      return {
        start: format(startOfMonth(now), 'yyyy-MM-dd'),
        end: format(now, 'yyyy-MM-dd')
      };
    } else if (dateRange === 'last-month') {
      return {
        start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
        end: format(endOfMonth(lastMonth), 'yyyy-MM-dd')
      };
    } else if (dateRange === 'this-year') {
      const now = new Date();
      return {
        start: format(startOfYear(now), 'yyyy-MM-dd'),
        end: format(now, 'yyyy-MM-dd')
      };
    } else {
      // "Last N days" window ending today (today inclusive) so freshly
      // published content shows up right away.
      const days = Number(dateRange);
      const end = new Date();
      const start = new Date(end);
      start.setDate(start.getDate() - ((Number.isFinite(days) && days > 0 ? days : 30) - 1));
      return {
        start: format(start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd')
      };
    }
  };

  const formattedDateRange = getFormattedDateRange();
  
  // Fetch analytics summary data
  const { 
    data: analyticsSummary, 
    isLoading: isSummaryLoading, 
    isError: isSummaryError, 
    error: summaryError 
  } = useAnalyticsSummary({
    start: formattedDateRange.start,
    end: formattedDateRange.end,
    brandId: selectedBrand && selectedBrand !== 'all' ? selectedBrand : undefined,
    applanguage: i18n.language,
    enabled: activeTab === 'summary' && !brandSelectionRequired,
    refresh: refreshQueryParam,
    withAnalysis: false
  });

  // Check if the selected brand has an account for the active platform
  const hasPlatformAccount = useMemo(() => {
    if (activeTab === 'summary') return true; // Always show summary tab
    
    // If no brand is selected or "all brands" is selected
    if (!selectedBrand) return connectedPages.some((page: ConnectedPage) => page.platform === activeTab);
    
    // Check if the selected brand has an account for the active platform
    return connectedPages.some(
      (page: ConnectedPage) => page.platform === activeTab && page.brandId === selectedBrand
    );
  }, [activeTab, selectedBrand, connectedPages]);

  // Extract pageId for the selected brand and platform
  const getPageIdForSelectedBrand = useMemo(() => {
    if (!selectedBrand || activeTab === 'summary' || !connectedPages.length) return undefined;
    
    const platformAccount = connectedPages.find(
      (page: ConnectedPage) => page.platform === activeTab && page.brandId === selectedBrand
    );
    
    console.log('Selected Brand:', selectedBrand);
    console.log('Active Tab (Platform):', activeTab);
    console.log('Connected Pages:', connectedPages);
    console.log('Found Platform Account:', platformAccount);
    console.log('PageId for API call:', platformAccount?.pageId);
    
    return platformAccount?.pageId;
  }, [selectedBrand, activeTab, connectedPages]);

  // Fetch platform-specific data when a platform tab is selected
  const {
    data: platformData,
    isLoading: isPlatformLoading,
    isError: isPlatformError,
    error: platformError
  } = usePlatformOverview({
    platform: (!brandSelectionRequired && activeTab !== 'summary') ? activeTab as SocialPlatform : null,
    start: formattedDateRange.start,
    end: formattedDateRange.end,
    brandId: selectedBrand && selectedBrand !== 'all' ? selectedBrand : undefined,
    pageId: getPageIdForSelectedBrand,
    hasPlatformAccount: !brandSelectionRequired && hasPlatformAccount,
    refresh: refreshQueryParam,
    withAnalysis: false
  });

  // On-demand AI Analysis Mutation
  const onDemandAiMutation = useOnDemandAiAnalysis();

  // --- Time series metric selection (per-platform view) ---
  const [selectedTimeSeriesMetric, setSelectedTimeSeriesMetric] = useState<string>('IMPRESSIONS');

  const availableTimeSeriesMetrics = useMemo<string[]>(() => {
    const timeData = (platformData as any)?.timeGroupedData as Array<{ date: string; values: Record<string, number> }> | undefined;
    if (!Array.isArray(timeData) || timeData.length === 0) return [];
    const allKeys = Array.from(
      timeData.reduce((set, point) => {
        Object.keys(point?.values || {}).forEach(k => set.add(k));
        return set;
      }, new Set<string>())
    );
    const withData = allKeys.filter((key) => timeData.some((p) => Number(p?.values?.[key] || 0) > 0));
    // Sort alphabetically by localized label
    return withData.slice().sort((a, b) => formatMetricLabel(a).localeCompare(formatMetricLabel(b)));
  }, [platformData]);

  // All available keys (even if all-zero) for fallback dropdown/plot
  const allTimeSeriesMetrics = useMemo<string[]>(() => {
    const timeData = (platformData as any)?.timeGroupedData as Array<{ date: string; values: Record<string, number> }> | undefined;
    if (!Array.isArray(timeData) || timeData.length === 0) return [];
    return Array.from(
      timeData.reduce((set, point) => {
        Object.keys(point?.values || {}).forEach(k => set.add(k));
        return set;
      }, new Set<string>())
    );
  }, [platformData]);

  useEffect(() => {
    // Reset selected metric when platform changes or metrics set changes
    if (availableTimeSeriesMetrics.length > 0) {
      if (!availableTimeSeriesMetrics.includes(selectedTimeSeriesMetric)) {
        // Prefer an engagement/impressions-like metric if available
        const preferred = ['IMPRESSIONS', 'VIEWS', 'POST_ENGAGEMENTS', 'LIKES', 'FOLLOWER_COUNT'];
        const found = preferred.find(p => availableTimeSeriesMetrics.includes(p));
        setSelectedTimeSeriesMetric(found || availableTimeSeriesMetrics[0]);
      }
    } else if (selectedTimeSeriesMetric) {
      setSelectedTimeSeriesMetric('');
    }
  }, [activeTab, availableTimeSeriesMetrics]);

  // Metric dropdown state and helpers (BrandDropdown-like UX)
  const [isMetricDropdownOpen, setIsMetricDropdownOpen] = useState(false);

  const timeSeriesData = (platformData as any)?.timeGroupedData as Array<{ date: string; values: Record<string, number> }> | undefined;
  const previousTimeSeriesData = (platformData as any)?.previousTimeGroupedData as Array<{ date: string; values: Record<string, number> }> | undefined;
  const formattedPreviousDateRange = useMemo(() => {
    const tf = (platformData as any)?.timeframes?.previous as { start?: string; end?: string } | undefined;
    if (!tf || !tf.start || !tf.end) return null;
    try {
      const start = new Date(tf.start);
      const end = new Date(tf.end);
      const toDdMmYyyy = (d: Date) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
      return `${toDdMmYyyy(start)} - ${toDdMmYyyy(end)}`;
    } catch {
      return null;
    }
  }, [platformData]);
  const metricsForDropdown = availableTimeSeriesMetrics.length > 0 ? availableTimeSeriesMetrics : allTimeSeriesMetrics;
  const sortedMetricsForDropdown = useMemo(() => (metricsForDropdown || []).slice().sort((a, b) => formatMetricLabel(a).localeCompare(formatMetricLabel(b))), [metricsForDropdown]);
  const fallbackMetric = metricsForDropdown[0] || 'IMPRESSIONS';
  const effectiveMetricKey = (selectedTimeSeriesMetric && (allTimeSeriesMetrics.includes(selectedTimeSeriesMetric) || metricsForDropdown.includes(selectedTimeSeriesMetric)))
    ? selectedTimeSeriesMetric
    : fallbackMetric;
  const isAllZeroForEffectiveMetric = useMemo(() => {
    const data = timeSeriesData || [];
    if (!Array.isArray(data) || data.length === 0) return true;
    return !data.some(p => Number(p?.values?.[effectiveMetricKey] || 0) > 0);
  }, [timeSeriesData, effectiveMetricKey]);

  // Helper: compute average value for a metric from timeSeriesData, ignoring zeros
  const computeAverageFromTimeSeries = (metricKey: string): number => {
    const series = Array.isArray(timeSeriesData) ? timeSeriesData : [];
    if (series.length === 0) return 0;
    const values = series
      .map(point => Number(point?.values?.[metricKey] || 0))
      .filter(v => v > 0);
    if (values.length === 0) return 0;
    const sum = values.reduce((acc, v) => acc + v, 0);
    return sum / values.length;
  };
  const [showAllSecondaryMetrics, setShowAllSecondaryMetrics] = useState(false);
  // Determine how many secondary metric cards to show by default depending on viewport
  const getVisibleSecondaryCount = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') return 4;
    return window.matchMedia('(min-width: 1280px)').matches ? 5 : 4; // xl breakpoint => 5 cols
  };
  const [secondaryVisibleCount, setSecondaryVisibleCount] = useState<number>(getVisibleSecondaryCount());
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1280px)');
    const handler = () => setSecondaryVisibleCount(mq.matches ? 5 : 4);
    // Initial sync (in case SSR or hydration differences)
    handler();
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
    } else if ((mq as any).addListener) {
      (mq as any).addListener(handler);
    }
    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener('change', handler);
      } else if ((mq as any).removeListener) {
        (mq as any).removeListener(handler);
      }
    };
  }, []);
  const topPosts = useMemo(() => {
    const source = activeTab === 'summary' ? (analyticsSummary as any)?.topPosts : (platformData as any)?.topPosts;
    const posts = source as any[] | undefined;
    if (!Array.isArray(posts)) return [];
    return posts.slice(0, 5);
  }, [analyticsSummary, platformData, activeTab]);

  // Minimal helper to infer media emoji from URL (aligns with calendar semantics)
  const getMediaEmojiFromUrl = (url?: string): string => {
    if (!url) return '📝';
    const lower = url.toLowerCase();
    if (/(\.mp4|\.mov|\.webm|\.avi|\.mkv|\.m4v)(\?|$)/.test(lower)) return '🎬';
    return '🖼️';
  };
  const getMediaLabelFromUrl = (url?: string): string => {
    if (!url) return 'Text';
    const lower = url.toLowerCase();
    if (/(\.mp4|\.mov|\.webm|\.avi|\.mkv|\.m4v)(\?|$)/.test(lower)) return 'Video';
    return 'Image';
  };

  // Prefer backend-provided post.type for media (video/image/text), fallback to URL inference
  const getMediaEmojiAndLabelFromPost = (post: any): { emoji: string; label: string } => {
    const rawType: string | undefined = post?.type || post?.content?.type;
    const normalized = (rawType || '').toString().toLowerCase();
    if (normalized === 'video') return { emoji: '🎬', label: 'Video' };
    if (normalized === 'image' || normalized === 'photo' || normalized === 'picture') return { emoji: '🖼️', label: 'Image' };
    if (normalized === 'text' || normalized === 'status') return { emoji: '📝', label: 'Text' };
    const url = post?.content?.media_url || post?.content?.thumbnail_url || post?.content?.permalink;
    return { emoji: getMediaEmojiFromUrl(url), label: getMediaLabelFromUrl(url) };
  };

  // Effect to update currentAiData when analyticsSummary or platformData changes (initial load)
  useEffect(() => {
    // This effect primarily handles AI data that might come with the *initial* lean fetch.
    // AI data from the on-demand POST call will be set via the mutation's onSuccess.
    let initialAiData = null;
    if (activeTab === 'summary' && analyticsSummary?.ai?.summary) {
      initialAiData = analyticsSummary.ai;
    } else if (activeTab !== 'summary' && platformData?.ai?.summary) {
      initialAiData = platformData.ai;
    }
    // Only set if not already populated by a successful on-demand fetch
    // and if it's different from what might already be there.
    if (!onDemandAiMutation.isSuccess) { // Avoid overwriting successful on-demand AI with initial load AI
        setCurrentAiData(initialAiData);
    }

  }, [analyticsSummary, platformData, activeTab, onDemandAiMutation.isSuccess]);

  // Update available brands when data is loaded - This useEffect will be refactored/removed for populating the dropdown.
  // It will now focus on auto-selecting a brand if none is in the URL.
  useEffect(() => {
    let brandIdToSuggest: string | null = null;

    // Suggest a brand based on current API context (summary or platform)
    if (activeTab === 'summary' && analyticsSummary?.currentBrand?.id) {
      brandIdToSuggest = analyticsSummary.currentBrand.id;
    } else if (activeTab !== 'summary' && platformData?.currentBrand?.id) {
      brandIdToSuggest = platformData.currentBrand.id;
    }

    // If a brand is suggested by an API and no brand is currently selected in the URL state
    if (!selectedBrand && brandIdToSuggest) {
      // Check if the suggested brand ID exists in the list of brands with connected accounts
      const isValidSuggestion = brandsWithConnectedAccounts.some(b => b.id === brandIdToSuggest);
      if (isValidSuggestion) {
        setSelectedBrand(brandIdToSuggest);
      } else if (brandsWithConnectedAccounts.length > 0 && activeTab === 'summary') { 
        // Fallback for summary: if API suggestion is invalid or not present, but we have brands, 
        // consider selecting the first one. This part is debatable UX.
        // For now, let's be conservative and only set if the API-suggested one is valid and from summary context.
        // Or if no specific context, just ensure something is selected if possible.
        // setSelectedBrand(brandsWithConnectedAccounts[0].id); 
      }
    }
    // Note: The dropdown now uses `brandsWithConnectedAccounts` which only shows brands with connected accounts.
  }, [
    analyticsSummary, 
    platformData, 
    brandsWithConnectedAccounts, 
    activeTab, 
    selectedBrand, 
    setSelectedBrand
  ]);

  // Update selectedPlatform when activeTab changes
  useEffect(() => {
    if (activeTab !== 'summary' && activeTab !== 'all') {
      setSelectedPlatform(activeTab as SocialPlatform);
    }
  }, [activeTab]);

  // Helper function to dynamically generate platform-specific metric titles
  function getDynamicPlatformTitle(type: string): string | null {
    // Extract platform and metric type from backend metric types
    const platformFollowerPattern = /^(\w+)_FOLLOWERS$/;
    const platformSubscriberPattern = /^(\w+)_SUBSCRIBERS$/;
    
    const followerMatch = type.match(platformFollowerPattern);
    const subscriberMatch = type.match(platformSubscriberPattern);
    
    if (followerMatch) {
      const platform = followerMatch[1].toLowerCase();
      // Map platform codes to display names
      const platformNames: Record<string, string> = {
        'instagram': 'Instagram',
        'facebook': 'Facebook', 
        'linkedin': 'LinkedIn',
        'tiktok': 'TikTok',
        'twitter': 'Twitter'
      };
      
      const platformName = platformNames[platform];
      if (platformName) {
        return t('analytics.metrics.dynamicFollowers', { platform: platformName });
      }
    }
    
    if (subscriberMatch) {
      const platform = subscriberMatch[1].toLowerCase();
      const platformNames: Record<string, string> = {
        'youtube': 'YouTube',
        'linkedin': 'LinkedIn'
      };
      
      const platformName = platformNames[platform];
      if (platformName) {
        return t('analytics.metrics.dynamicSubscribers', { platform: platformName });
      }
    }
    
    return null;
  }

  // Helper to format metric type and get icon/color, now fully internationalized
  function getMetricDisplayDetails(type: string) {
    // First, check if this is a dynamic platform-specific metric
    const dynamicTitle = getDynamicPlatformTitle(type);
    
    // Map backend metric types to i18n keys
    const metricKeyMap: Record<string, string> = {
      FOLLOWERS: 'followers',
      TOTAL_FOLLOWERS: 'followers',
      TOTAL_FOLLOWERS_COUNT: 'followers',
      FOLLOWER_COUNT: 'followers',
      ORGANIC_FOLLOWERS: 'organicFollowers',
      PAID_FOLLOWERS: 'paidFollowers',
      POSTS: 'posts',
      TOTAL_POSTS: 'posts',
      IMPRESSIONS: 'impressions',
      TOTAL_IMPRESSIONS: 'impressions',
      UNIQUE_IMPRESSIONS: 'uniqueImpressions',
        UNIQUE_VIEWS: 'uniqueViews',
      CLICKS: 'clicks',
        PAGE_CLICKS: 'pageClicks',
      REACTIONS: 'reactions',
      COMMENTS: 'comments',
      ENGAGEMENT: 'totalEngagement',
      REPOSTS: 'reposts',
      CTR: 'ctr',
      ENGAGEMENT_RATE: 'engagementRate',
      AVG_ENGAGEMENT_RATE: 'engagementRate',
      PAGE_VIEWS: 'pageViews',
      TOTAL_PAGE_VIEWS: 'pageViews',
      UNIQUE_PAGE_VIEWS: 'uniquePageViews',
      VIEWS: 'views',
      LIKES: 'likes',
      DISLIKES: 'dislikes',
      ESTIMATEDMINUTESWATCHED: 'minutesWatched',
      AVERAGEVIEWDURATION: 'averageViewDuration',
      TOTAL_ENGAGEMENT: 'totalEngagement',
      NEW_FOLLOWERS: 'newFollowers',
      FOLLOWERS_COUNT: 'followers',
      ORGANIC_FOLLOWER_GAIN: 'organicFollowers',
      PAID_FOLLOWER_GAIN: 'paidFollowers',
      NEWSUBSCRIBERS: 'newSubscribers',
      PROFILE_VIEWS: 'profileViews',
      ACCOUNTS_ENGAGED: 'accountsEngaged',
      SAVES: 'saves',
      SHARES: 'shares',
      WEBSITE_CLICKS: 'websiteClicks',
      // YouTube specific
      VIDEOSPOSTED: 'videosPosted',
      VIDEOS_POSTED: 'videosPosted',
      FOLLOWSANDUNFOLLOWS: 'followsAndUnfollows',
      FOLLOWS_AND_UNFOLLOWS: 'followsAndUnfollows',
      // Posts published (various spellings)
      POSTS_PUBLISHED: 'postsPublished',
      POSTSPUBLISHED: 'postsPublished',
      MEDIA_COUNT: 'mediaCount',
      REACH: 'reach',
      // Facebook UPPER_SNAKE_CASE metrics
      FAN_ADDS: 'fanAdds',
      FAN_REMOVES: 'fanRemoves',
      IMPRESSIONS_PAID: 'impressionsPaid',
      IMPRESSIONS_PAID_UNIQUE: 'impressionsPaidUnique',
      IMPRESSIONS_UNIQUE: 'impressionsUnique',
      IMPRESSIONS_VIRAL: 'impressionsViral',
      IMPRESSIONS_VIRAL_UNIQUE: 'impressionsViralUnique',
      POST_ENGAGEMENTS: 'postEngagements',
      POSTS_IMPRESSIONS: 'postImpressions',
      TOTAL_ACTIONS: 'totalActions',
      LOSTSUBSCRIBERS: 'lostSubscribers',
      SUBSCRIBERSGAIN: 'subscribersGain',
      SUBSCRIBERSGAINED: 'subscribersGained',
      SUBSCRIBERSLOST: 'subscribersLost',
      CONNECTED_PLATFORMS: 'connectedPlatforms',
    };
    
    // Use dynamic title if available, otherwise use i18n key mapping
    const key = metricKeyMap[type] || type.replace(/_/g, '').toLowerCase();
    let title = dynamicTitle || t(`analytics.metrics.${key}`, key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()));
    
    if (type === 'NEW_FOLLOWERS') {
      title = t('analytics.metrics.followerGrowth', 'Follower Growth'); // Specific title for this metric type
    }

    switch (type) {
      case 'FOLLOWERS': case 'TOTAL_FOLLOWERS': case 'FOLLOWER_COUNT': case 'TOTAL_FOLLOWERS_COUNT': return { title, icon: Users, color: 'bg-blue-500' };
      case 'ORGANIC_FOLLOWERS': return { title, icon: Users, color: 'bg-green-500' };
      case 'PAID_FOLLOWERS': return { title, icon: DollarSign, color: 'bg-purple-500' };
      case 'POSTS': case 'TOTAL_POSTS': return { title, icon: FileText, color: 'bg-orange-500' };
      case 'IMPRESSIONS': case 'TOTAL_IMPRESSIONS': return { title, icon: Eye, color: 'bg-indigo-500' };
      case 'UNIQUE_IMPRESSIONS': return { title, icon: Eye, color: 'bg-cyan-500' };
      case 'CLICKS': return { title, icon: MousePointer, color: 'bg-emerald-500' };
      case 'REACTIONS': return { title, icon: Smile, color: 'bg-yellow-500' };
      case 'COMMENTS': return { title, icon: MessageSquare, color: 'bg-amber-500' };
      case 'ENGAGEMENT': return { title, icon: ThumbsUp, color: 'bg-green-600' };
      case 'REPOSTS': return { title, icon: Send, color: 'bg-red-500' };
      case 'CTR': return { title, icon: BarChart, color: 'bg-pink-500' };
      case 'ENGAGEMENT_RATE': case 'AVG_ENGAGEMENT_RATE': return { title, icon: Percent, color: 'bg-rose-500' };
      case 'PAGE_VIEWS': case 'TOTAL_PAGE_VIEWS': return { title, icon: FileText, color: 'bg-slate-500' };
      case 'UNIQUE_PAGE_VIEWS': return { title, icon: Hash, color: 'bg-gray-500' };
      case 'UNIQUE_VIEWS': return { title, icon: Eye, color: 'bg-cyan-500' };
      case 'VIEWS': return { title, icon: Eye, color: 'bg-indigo-500' };
      case 'LIKES': return { title, icon: ThumbsUp, color: 'bg-green-500' };
      case 'DISLIKES': return { title, icon: MinusCircle, color: 'bg-red-500' };
      case 'ESTIMATEDMINUTESWATCHED': return { title, icon: BarChart, color: 'bg-purple-500' };
      case 'AVERAGEVIEWDURATION': return { title, icon: BarChart, color: 'bg-teal-500' };
      case 'TOTAL_ENGAGEMENT': return { title, icon: ThumbsUp, color: 'bg-green-500' };
      case 'FOLLOWERS_COUNT': return { title, icon: Users, color: 'bg-blue-600' };
      case 'ORGANIC_FOLLOWER_GAIN': return { title, icon: Users, color: 'bg-green-600' };
      case 'PAID_FOLLOWER_GAIN': return { title, icon: DollarSign, color: 'bg-purple-600' };
      case 'NEWSUBSCRIBERS': return { title, icon: Users, color: 'bg-sky-500' };
      case 'ACCOUNTS_ENGAGED': return { title, icon: Users, color: 'bg-blue-400' };
      case 'MEDIA_COUNT': return { title, icon: FileText, color: 'bg-orange-400' };
      case 'POSTS_PUBLISHED': case 'POSTSPUBLISHED': return { title, icon: NotebookPen, color: 'bg-orange-500' };
      case 'PROFILE_VIEWS': return { title, icon: Eye, color: 'bg-cyan-400' };
      case 'REACH': return { title, icon: BarChart, color: 'bg-indigo-400' };
      case 'SAVES': return { title, icon: FileText, color: 'bg-green-400' };
      case 'SHARES': return { title, icon: Send, color: 'bg-purple-400' };
      case 'WEBSITE_CLICKS': return { title, icon: MousePointer, color: 'bg-pink-400' };
      case 'VIDEOSPOSTED': case 'VIDEOS_POSTED': return { title, icon: FileText, color: 'bg-orange-500' };
      case 'FOLLOWSANDUNFOLLOWS': case 'FOLLOWS_AND_UNFOLLOWS': return { title, icon: Users, color: 'bg-sky-500' };
      // Facebook UPPER_SNAKE_CASE metrics
      case 'FAN_ADDS': case 'FAN_REMOVES': return { title, icon: Users, color: 'bg-blue-500' };
      case 'IMPRESSIONS_PAID': case 'IMPRESSIONS_PAID_UNIQUE': case 'IMPRESSIONS_UNIQUE':
      case 'IMPRESSIONS_VIRAL': case 'IMPRESSIONS_VIRAL_UNIQUE': return { title, icon: Eye, color: 'bg-indigo-500' };
      case 'POST_ENGAGEMENTS': return { title, icon: ThumbsUp, color: 'bg-green-500' };
      case 'POSTS_IMPRESSIONS': return { title, icon: Eye, color: 'bg-indigo-400' };
      case 'TOTAL_ACTIONS': return { title, icon: BarChart, color: 'bg-orange-500' };
      case 'LOSTSUBSCRIBERS': return { title, icon: Users, color: 'bg-red-500' };
      case 'SUBSCRIBERSGAIN': return { title, icon: Users, color: 'bg-green-500' };
      case 'SUBSCRIBERSGAINED': return { title, icon: Users, color: 'bg-green-500' };
      case 'SUBSCRIBERSLOST': return { title, icon: Users, color: 'bg-red-500' };
      case 'CONNECTED_PLATFORMS': return { title, icon: FileText, color: 'bg-teal-500' };
      case 'PAGE_CLICKS': return { title, icon: MousePointer, color: 'bg-emerald-500' };
      case 'NEW_FOLLOWERS': return { title, icon: Users, color: 'bg-sky-600' }; // Use specific title from above
      
      // Dynamic platform-specific cases (now handled by getDynamicPlatformTitle)
      case 'INSTAGRAM_FOLLOWERS': case 'FACEBOOK_FOLLOWERS': case 'LINKEDIN_FOLLOWERS': 
      case 'TIKTOK_FOLLOWERS': case 'TWITTER_FOLLOWERS': return { title, icon: Users, color: 'bg-pink-600' };
      case 'YOUTUBE_SUBSCRIBERS': return { title, icon: Users, color: 'bg-red-700' };
      
      default: 
        console.warn("Unknown metric type for display:", type);
        return { title, icon: BarChart, color: 'bg-gray-500' };
    }
  }

  // Format a metric key into localized label (used by dropdown sorting)
  function formatMetricLabel(key: string) {
    const details = getMetricDisplayDetails(key);
    return details?.title || key.replace(/_/g, ' ').toLowerCase().replace(/^.|\s\w/g, (c) => c.toUpperCase());
  }

  const parseChangePercentage = (variationsPercentage: string | number): number => {
    if (typeof variationsPercentage === 'number') return variationsPercentage; // Already a number
    if (variationsPercentage === "∞" || variationsPercentage === "Infinity") return Infinity;
    if (variationsPercentage === "-∞" || variationsPercentage === "-Infinity") return -Infinity;
    const num = parseFloat(variationsPercentage);
    return isNaN(num) ? 0 : num;
  };

  // Generate summary metrics from API data
  const getSummaryMetrics = (): DisplayMetric[] => {
    const sourceKeyMetrics = (analyticsSummary as any)?.keyMetrics;

    if (!sourceKeyMetrics || !Array.isArray(sourceKeyMetrics) || sourceKeyMetrics.length === 0) {
      console.warn('Analytics summary data (analyticsSummary.keyMetrics) not available or empty. Using fallback mock data.');
      return mockSummaryMetrics.map(m => ({ 
        ...m,
        rawValueChange: m.change?.toString() ?? "0"
      }));
    }

    const keyMetrics = sourceKeyMetrics as BackendMetricItem[];
    const findMetric = (type: string): BackendMetricItem | undefined => keyMetrics.find(m => m.type === type);
    const metricsToDisplay: DisplayMetric[] = [];

    // Define the metrics we expect FOR THE KEY METRICS SECTION from the summary
    const summaryMetricDefinitions: Array<{ type: string; i18nKey: string; defaultTitle: string; icon: React.ElementType; color: string; oldMockTitle?: string }> = [
      {
        type: 'TOTAL_FOLLOWERS_COUNT',
        i18nKey: 'analytics.metrics.totalFollowersCount',
        defaultTitle: 'Total Followers',
        icon: Users,
        color: 'bg-blue-500',
        oldMockTitle: 'Total Followers'
      },
      // TOTAL_ENGAGEMENT is now in thirdMetrics
      // CONNECTED_PLATFORMS is now in thirdMetrics
      {
        type: 'VIEWS', // API provides 'VIEWS'
        i18nKey: 'analytics.metrics.views',
        defaultTitle: 'Total Views',
        icon: Eye,
        color: 'bg-indigo-500',
        oldMockTitle: 'Total Impressions'
      },
      {
        type: 'ENGAGEMENT_RATE', // Use ENGAGEMENT_RATE from keyMetrics
        i18nKey: 'analytics.metrics.engagementRate', // Ensure this i18n key covers general Engagement Rate
        defaultTitle: 'Engagement Rate',
        icon: Percent, 
        color: 'bg-rose-500',
        oldMockTitle: 'Avg. Engagement Rate' // Mock used 'Avg. Engagement Rate' 
      }
      // NEW_FOLLOWERS is now in thirdMetrics
    ];

    summaryMetricDefinitions.forEach(def => {
      const metric = findMetric(def.type);
      if (metric) {
        metricsToDisplay.push({
          title: t(def.i18nKey, def.defaultTitle),
          value: metric.total,
          previousValue: metric.previousTotal,
          change: parseChangePercentage(metric.variationsPercentage),
          icon: def.icon,
          color: def.color,
          rawValueChange: metric.variationsPercentage?.toString(),
          isPercentage: def.type === 'ENGAGEMENT_RATE',
          description: (metric as any).description
        });
      } else {
        console.warn(`Metric type "${def.type}" not found in summary keyMetrics. Card will not be displayed.`);
      }
    });

    // If after attempting to build from API, metricsToDisplay is empty, 
    // AND the API did return some keyMetrics (but none matched our definitions),
    // it's different from the API returning nothing. 
    // However, the current fallback above handles the API returning nothing.
    // If metricsToDisplay is empty here, it means specific expected metrics were not found.
    // This is now the desired behavior: show nothing for those missing expected metrics.

    return metricsToDisplay;
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleBrandChange = (brandId: string) => {
    setSelectedBrand(brandId);
  };

  const handleDateRangeChange = (newDateRange: 'this-month' | 'last-month' | 'this-year' | number | 'custom') => {
    setDateRange(newDateRange);
  };

  const handleCustomDateRangeChange = (range: { start: Date; end: Date }) => {
    setCustomDateRange(range);
    setDateRange('custom');
  };

  // Modify getAIInsights to use the new structure
  const getAIInsights = () => {
    const noAIDataMessage = t('analytics.ai.noData', 'AI analysis is not available for this period.');
    const noStrengthsMessage = t('analytics.ai.noStrengths', 'No specific strengths identified yet.');
    // Using "weaknesses" for what was "improvements"
    const noWeaknessesMessage = t('analytics.ai.noImprovements', 'No specific weaknesses identified yet.'); 

    if ((analyticsSummary as any)?.ai?.error && !onDemandAiMutation.isPending && !currentAiData) {
      return {
        summary: t('analytics.ai.errorAnalysis', 'AI analysis encountered an error. You can try generating it again.'),
        strengths: [noStrengthsMessage],
        weaknesses: [noWeaknessesMessage],
        showRequestButton: true // Allow retry
      }
    }
    
    if (!currentAiData && !onDemandAiMutation.isPending) { 
        return {
            summary: t('analytics.ai.requestPrompt', 'Click to generate AI analysis.'),
            strengths: [noStrengthsMessage],
            weaknesses: [noWeaknessesMessage],
            showRequestButton: true 
        };
    }
    
    if (!currentAiData) { 
        return {
            summary: noAIDataMessage,
            strengths: [noStrengthsMessage],
            weaknesses: [noWeaknessesMessage],
            showRequestButton: false 
        };
    }
    
    // The API returns "strenghts", we map it to "strengths" for our internal use
    const strengthsList = currentAiData.strenghts && currentAiData.strenghts.length > 0 
      ? currentAiData.strenghts 
      : [noStrengthsMessage];
      
    const weaknessesList = currentAiData.weaknesses && currentAiData.weaknesses.length > 0
      ? currentAiData.weaknesses
      : [noWeaknessesMessage];

    return {
        summary: currentAiData.summary || noAIDataMessage,
        strengths: strengthsList,
        weaknesses: weaknessesList, // Mapped weaknesses to the 'improvements' UI slot
        showRequestButton: false
    };
  };

  const currentAIInsights = getAIInsights();

  // --- Summary tab: multi-platform time series state ---
  const aggregatedForSummary = (analyticsSummary as any)?.aggregatedDailyMetrics as Array<{ date: string; metrics: Record<string, { total: number; platforms: Record<string, number | null> }> }> | undefined;
  const availableSummaryMetricKeys = useMemo<string[]>(() => {
    if (!Array.isArray(aggregatedForSummary) || aggregatedForSummary.length === 0) return [];
    return Array.from(
      aggregatedForSummary.reduce((set, entry) => {
        Object.keys(entry?.metrics || {}).forEach(k => set.add(k));
        return set;
      }, new Set<string>())
    );
  }, [aggregatedForSummary]);
  const availableSummaryMetricKeysWithData = useMemo<string[]>(() => {
    if (!Array.isArray(aggregatedForSummary) || aggregatedForSummary.length === 0) return [];
    const allKeys = new Set<string>();
    aggregatedForSummary.forEach(entry => {
      Object.keys(entry?.metrics || {}).forEach(k => allKeys.add(k));
    });
    const keys: string[] = Array.from(allKeys);
    return keys.filter((key) => (
      (aggregatedForSummary || []).some((entry) => {
        const item = entry?.metrics?.[key];
        if (!item) return false;
        const totalVal = Number(item.total ?? 0);
        if (totalVal > 0) return true;
        const platforms = item.platforms || {};
        return Object.values(platforms).some((v) => Number(v ?? 0) > 0);
      })
    ));
  }, [aggregatedForSummary]);
  const [selectedSummaryMetric, setSelectedSummaryMetric] = useState<string>(() => {
    const initialOptions = (availableSummaryMetricKeysWithData.length > 0) ? availableSummaryMetricKeysWithData : availableSummaryMetricKeys;
    if (initialOptions.includes('VIEWS')) return 'VIEWS';
    return initialOptions[0] || '';
  });
  useEffect(() => {
    // Ensure selected metric stays valid when data changes
    const metricOptions = (availableSummaryMetricKeysWithData.length > 0) ? availableSummaryMetricKeysWithData : availableSummaryMetricKeys;
    if (metricOptions.length === 0) {
      setSelectedSummaryMetric('');
      return;
    }
    if (!metricOptions.includes(selectedSummaryMetric)) {
      setSelectedSummaryMetric(metricOptions.includes('VIEWS') ? 'VIEWS' : metricOptions[0]);
    }
  }, [availableSummaryMetricKeys, availableSummaryMetricKeysWithData]);
  const [isSummaryMetricDropdownOpen, setIsSummaryMetricDropdownOpen] = useState<boolean>(false);

  const handleRequestAiAnalysis = () => {
    const sourceData = activeTab === 'summary' ? analyticsSummary : platformData;
    const currentFormattedDateRange = getFormattedDateRange();

    if (!sourceData) {
      console.error('Source data is not available for AI analysis.');
      // Attempt to set AI card to a state where user can request
      if (!currentAiData) { // Check if AI data is already absent
        setCurrentAiData(null); // Explicitly nullify to show request button via getAIInsights logic
      }
      return;
    }
    
    let metricsForAI: { type: string; total: number; previousTotal: number; variationsPercentage: string; }[] = [];

    if (activeTab === 'summary') {
      // Use keyMetrics from analyticsSummary for the new structure
      if (!analyticsSummary || !(analyticsSummary as any).keyMetrics) {
        console.error('Summary keyMetrics data is not available for AI analysis.');
        if (!currentAiData) {
            setCurrentAiData(null); 
        }
        return;
      }
      metricsForAI = (analyticsSummary as any).keyMetrics.map((metric: BackendMetricItem) => ({
        type: metric.type,
        total: metric.total,
        previousTotal: metric.previousTotal,
        variationsPercentage: metric.variationsPercentage?.toString() || "0", // Ensure it's a string
      }));
    } else if (platformData) {
      // Cast platformData to include keyMetrics and secondaryMetrics, assuming new API structure
      const typedPlatformData = platformData as any as { keyMetrics?: BackendMetricItem[], secondaryMetrics?: BackendMetricItem[] };
      
      if (!typedPlatformData.keyMetrics && !typedPlatformData.secondaryMetrics) {
        console.error('Platform metrics data (keyMetrics or secondaryMetrics) is not available for AI analysis.');
        return;
      }
      metricsForAI = (typedPlatformData.keyMetrics || []).concat(typedPlatformData.secondaryMetrics || []).map((metric: BackendMetricItem) => {
        return {
          type: metric.type, // Already in correct format (e.g., FOLLOWERS)
          total: metric.total,
          previousTotal: metric.previousTotal,
          variationsPercentage: metric.variationsPercentage,
        };
      });
    } else {
      console.error('No data source available for AI analysis.');
      return;
    }
    
    if (!metricsForAI || metricsForAI.length === 0) {
      console.error('No valid metrics found for AI analysis.');
      return;
    }

    const currentTimestamps = {
      start: new Date(currentFormattedDateRange.start).getTime(),
      end: new Date(currentFormattedDateRange.end).getTime(),
    };

    // Construct the inner data object based on the curl example
    // Omitting 'expiresAt', 'payload.success', and 'debug.previousTimeframe' for now
    // as their source/necessity is unclear.
    const innerDataObject = {
      payload: {
        metrics: metricsForAI,
        debug: {
          currentTimeframe: currentTimestamps,
          // previousTimeframe: {} // Leaving out previousTimeframe for now
        }
      }
    };

    const dataStringForApi = JSON.stringify(innerDataObject);
    
    onDemandAiMutation.mutate({
      appLanguage: i18n.language || 'en',
      platform: activeTab === 'summary' ? undefined : activeTab as SocialPlatform, 
      data: dataStringForApi, 
    }, {
      onSuccess: (response) => {
        console.log('On-demand AI Analysis successful:', response);
        setCurrentAiData(response.analysis); 
      },
      onError: (error) => {
        console.error('On-demand AI Analysis error:', error);
        alert(t('analytics.ai.errorOnDemand', 'Failed to get AI analysis. Please try again.'));
        setCurrentAiData(null); 
      }
    });
  };
  
  // Define a comprehensive loading state for the main page content
  const isPageLoading = (activeTab === 'summary' && isSummaryLoading) || 
                        (activeTab !== 'summary' && isPlatformLoading) || 
                        isAccountsLoading || isLoadingAllBrands;

  // Loading state for the AI section specifically when on-demand AI is being fetched.
  const isAiCardLoading = onDemandAiMutation.isPending;

  // Determine if there's an error for the main page data
  const isError = (activeTab === 'summary' && isSummaryError) || 
                 (activeTab !== 'summary' && isPlatformError && hasPlatformAccount);
  
  // Get the appropriate error message
  const errorMessage = activeTab === 'summary' 
    ? summaryError?.message 
    : platformError?.message;

  return (
    <PageLayout
      title={t('analytics.title')}
      subtitle={t('analytics.subtitle')}
      actions={
        <div className="w-full flex flex-col items-start sm:items-end gap-3">
          <button
            onClick={handleRequestAiAnalysis}
            disabled={isAiCardLoading}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
          >
            {isAiCardLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t('analytics.ai.analyzing', 'Analyzing...')}
              </>
            ) : (
              <>
                <Wand2 className="w-5 h-5 mr-2" />
                {t('analytics.ai.analyzeButton', 'Analyze Now')}
              </>
            )}
          </button>

          <div className="flex flex-row flex-wrap items-center gap-3 w-full sm:w-auto justify-start sm:justify-end">
            <BrandDropdown
              brands={brandsWithConnectedAccounts || []}
              selectedBrandId={selectedBrand || ''}
              onBrandSelect={handleBrandChange}
              showRedirectionCard={false}
              isLoading={isLoadingAllBrands}
            />

            <DateRangePicker
              dateRange={dateRange}
              customDateRange={customDateRange}
              onDateRangeChange={handleDateRangeChange}
              onCustomDateRangeChange={handleCustomDateRangeChange}
              baseDate={new Date()}
              fullWidth={false}
            />
          </div>
        </div>
      }
    >

          

          <div className="mb-6">
            <SocialMediaTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              showSummary={true}
            />
          </div>

          {/* Show redirection card if no accounts are connected */}
          {!isAccountsLoading && !hasConnectedAccounts ? (
            <div className="mb-8">
              <LinkBrandRedirectionCard 
                message={t('analytics.connectAccountFirst', 'Connect a social media account to view analytics')}
                isLoading={isAccountsLoading}
              />
            </div>
          ) : activeTab !== 'summary' && !hasPlatformAccount ? (
            <div className="mb-8">
              <LinkBrandRedirectionCard 
                message={t('analytics.connectAccountForPlatform', {
                  platform: platforms.find(p => p.id === activeTab)?.name || activeTab
                })}
                isLoading={isAccountsLoading || isPlatformLoading}
              />
            </div>
          ) : brandSelectionRequired ? (
            <div className="mb-8 flex justify-center">
              <div className="rounded-xl border border-black shadow-lg bg-white p-6 w-[95%] sm:w-full sm:max-w-[60%]">
                <h3 className="text-lg font-semibold mb-2">{t('analytics.selectBrand.title', 'Choose a brand')}</h3>
                <p className="text-sm text-gray-600 mb-4">{t('analytics.selectBrand.subtitle', 'Select a brand to view analytics. This avoids unnecessary data fetching.')}</p>
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {brandsWithConnectedAccounts.map((b: Brand) => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => handleBrandChange(b.id)}
                      className="flex flex-col items-center gap-2 p-4 border border-black rounded-lg hover:bg-gray-50 text-center"
                    >
                      {b.logo ? (
                        <img src={b.logo} alt={b.name} className="w-12 h-12 rounded object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded bg-gray-200" />
                      )}
                      <span className="text-sm font-medium">{b.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : isPageLoading ? (
            <div className="space-y-8">
              {/* Time series + main metrics skeleton */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Time series card */}
                <div className="lg:col-span-3 rounded-xl border border-black shadow-lg bg-white overflow-hidden animate-pulse">
                  <div className="p-6 border-b border-gray-200 bg-gray-100">
                    <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                  </div>
                  <div className="p-4">
                    <div className="h-64 bg-gray-100 rounded"></div>
                  </div>
                </div>
                {/* Main metric cards */}
                <div className="space-y-4">
                  {Array(2).fill(0).map((_, index) => (
                    <div key={`metric-skel-${index}`} className="rounded-xl border border-black shadow-lg bg-white p-4 animate-pulse">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-8 w-8 bg-gray-200 rounded"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* More Platform Metrics skeleton */}
              <div className="space-y-4">
                <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {Array(4).fill(0).map((_, index) => (
                    <div key={`more-skel-${index}`} className="rounded-xl p-4 border border-black shadow-lg bg-white animate-pulse">
                      <div className="flex items-center justify-between mb-3">
                        <div className="h-8 w-8 bg-gray-200 rounded"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        <div className="h-6 bg-gray-200 rounded w-2/3"></div>
                        <div className="h-2 bg-gray-200 rounded w-1/3"></div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Toggle skeleton */}
                <div className="flex justify-center">
                  <div className="h-8 w-56 bg-gray-100 border border-gray-200 rounded-full animate-pulse"></div>
                </div>
              </div>

              {/* Top posts skeleton */}
              <div className="space-y-4 pb-8">
                <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {Array(3).fill(0).map((_, index) => (
                    <div key={`toppost-skel-${index}`} className="flex gap-3 p-3 border border-black rounded-lg bg-white shadow animate-pulse">
                      <div className="w-20 h-20 bg-gray-200 rounded-md"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                        <div className="flex gap-2">
                          <div className="h-5 w-12 bg-gray-100 border border-gray-200 rounded-full"></div>
                          <div className="h-5 w-16 bg-gray-100 border border-gray-200 rounded-full"></div>
                          <div className="h-5 w-14 bg-gray-100 border border-gray-200 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : isError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
              <h3 className="text-red-800 font-medium">Error loading analytics data</h3>
              <p className="text-red-600 text-sm mt-1">{errorMessage || 'An unknown error occurred'}</p>
            </div>
          ) : (
            <>
              {/* AI Analysis card now appears only after a successful analysis */}
              {!currentAIInsights.showRequestButton && !isAiCardLoading && currentAIInsights.summary && (
                <div className="mb-8 rounded-xl border border-black shadow-lg bg-white overflow-hidden">
                  <div className="p-6 border-b border-gray-200" style={{ backgroundColor: "#F2F0FD" }}>
                    <div className="flex items-center gap-3">
                      <Sparkles className="w-6 h-6 text-indigo-600" />
                      <h2 className="text-lg font-semibold text-gray-900 font-title">
                        {activeTab === 'summary' 
                          ? t('analytics.ai.title', 'AI Performance Analysis') 
                          : (
                            <div className="flex items-center">
                              <SocialIcon 
                                platform={selectedPlatform} 
                                size={20} 
                                className="mr-2" 
                              />
                              {t('analytics.ai.platformAnalysisTitle', `${platforms.find(p => p.id === activeTab)?.name || 'Platform'} Analysis`, { platform: platforms.find(p => p.id === activeTab)?.name || 'Platform' })}
                            </div>
                          )}
                      </h2>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex flex-col">
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-indigo-900 mb-3">{t('analytics.ai.summary','Summary')}</h3>
                        <p className="text-gray-700 whitespace-pre-line">{currentAIInsights.summary}</p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1 border-2 border-green-100 rounded-lg p-4">
                          <h3 className="text-sm font-medium text-green-600 mb-2 flex items-center whitespace-nowrap">
                            <div className="w-1 h-4 bg-green-500 mr-2 rounded-full"></div>
                            {t('analytics.ai.strengths', 'Points Forts')}
                          </h3>
                          {currentAIInsights.strengths.length > 0 ? (
                            <ul className="list-disc list-inside text-gray-700 space-y-1">
                              {currentAIInsights.strengths.map((item, index) => (
                                <li key={index}>{item}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-gray-700">{t('analytics.ai.noStrengths', 'No specific strengths identified yet.')}</p>
                          )}
                        </div>
                        <div className="flex-1 border-2 border-orange-100 rounded-lg p-4">
                          <h3 className="text-sm font-medium text-orange-600 mb-2 flex items-center whitespace-nowrap">
                            <div className="w-1 h-4 bg-orange-500 mr-2 rounded-full"></div>
                            {t('analytics.ai.improvements', 'Axes d\'Amélioration')} 
                          </h3>
                          {currentAIInsights.weaknesses.length > 0 ? (
                            <ul className="list-disc list-inside text-gray-700 space-y-1">
                              {currentAIInsights.weaknesses.map((item, index) => (
                                <li key={index}>{item}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-gray-700">{t('analytics.ai.noImprovements', 'No specific areas for improvement identified yet.')}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-8 grid grid-cols-1 lg:grid-cols-4 gap-4">
                <div className="lg:col-span-3">
                  {activeTab === 'summary' ? (
                    // New Summary multi-platform chart in the main grid position
                      (() => {
            const metricOptions = (availableSummaryMetricKeysWithData.length > 0) ? availableSummaryMetricKeysWithData : availableSummaryMetricKeys;
                      const sortedMetricOptions = metricOptions.slice().sort((a, b) => formatMetricLabel(a).localeCompare(formatMetricLabel(b)));
                      const effectiveSummaryMetric = (selectedSummaryMetric && metricOptions.includes(selectedSummaryMetric)) ? selectedSummaryMetric : (sortedMetricOptions[0] || 'VIEWS');
                      const platformIds = Array.from((aggregatedForSummary || []).reduce((set, entry) => {
                        const item = entry?.metrics?.[effectiveSummaryMetric];
                        if (item && item.platforms) Object.keys(item.platforms).forEach(p => set.add(p));
                        return set;
                      }, new Set<string>()));
                      const summarySeries = (aggregatedForSummary || []).map((entry) => {
                        const values: Record<string, number> = {};
                        const item = entry?.metrics?.[effectiveSummaryMetric];
                        for (const pid of platformIds) values[pid] = Number(item?.platforms?.[pid] ?? 0);
                        return { date: entry.date, values };
                      });
                      const platformColors: Record<string, string> = {
                        tiktok: '#000000',
                        youtube: '#ff0132',
                        facebook: '#0765ff',
                        instagram: '#9a1855',
                        linkedin: '#0270ae',
                      };
                      const dateLocaleObj = (i18n.language?.startsWith('fr') ? fr : i18n.language?.startsWith('de') ? de : i18n.language?.startsWith('it') ? itLocale : i18n.language?.startsWith('es') ? esLocale : i18n.language?.startsWith('pt') ? ptBR : enUS);
                      const details = getMetricDisplayDetails(effectiveSummaryMetric);
                      const hasData = Array.isArray(aggregatedForSummary) && aggregatedForSummary.length > 0 && platformIds.length > 0;
                      if (!hasData) return null;
                      return (
                        <div className="rounded-xl border border-black shadow-lg bg-white overflow-hidden h-full">
                          <div className="p-6 border-b border-gray-200" style={{ backgroundColor: '#F2F0FD' }}>
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <h2 className="text-lg font-semibold text-gray-900 font-title">{t('analytics.timeSeries.summaryTitle', 'Daily evolution by platform')}</h2>
                              <div className="ml-auto flex items-center gap-3">
                                {/* Granularity toggle */}
                                <div className="inline-flex border border-black rounded-lg overflow-hidden">
                                  <button type="button" onClick={() => setTimeGrouping('daily')} className={`px-3 py-1 text-xs ${timeGrouping === 'daily' ? 'bg-black text-white' : 'bg-white text-gray-700'}`}>
                                    {t('analytics.timeSeries.daily', 'Daily')}
                                  </button>
                                  <button type="button" onClick={() => setTimeGrouping('weekly')} className={`px-3 py-1 text-xs border-l border-black ${timeGrouping === 'weekly' ? 'bg-black text-white' : 'bg-white text-gray-700'}`}>
                                    {t('analytics.timeSeries.weekly', 'Weekly')}
                                  </button>
                                </div>
                                 {sortedMetricOptions.length > 0 && (
                                <div className="relative min-w-60">
                                  <button type="button" onClick={() => setIsSummaryMetricDropdownOpen(!isSummaryMetricDropdownOpen)} className="w-full flex items-center justify-between gap-2 px-4 py-2 rounded-lg hover:text-primary-600 transition-colors bg-white border-[0.5px] border-black">
                                    <span className="text-sm text-gray-700 truncate">{formatMetricLabel(effectiveSummaryMetric)}</span>
                                    <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                  </button>
                                  {isSummaryMetricDropdownOpen && (
                                    <>
                                      <div className="fixed inset-0 z-10" onClick={() => setIsSummaryMetricDropdownOpen(false)} />
                                      <div className="absolute left-0 right-0 mt-2 w-full rounded-lg bg-white shadow-lg z-20 border-[0.5px] border-black">
                                        <div className="py-1 max-h-[28rem] overflow-y-auto">
                                            {sortedMetricOptions.map((key) => (
                                              <button type="button" key={`summary-${key}`} onClick={() => { setSelectedSummaryMetric(key); setIsSummaryMetricDropdownOpen(false); }} className={`w-full flex items-center gap-2 px-4 py-2 text-sm ${key === effectiveSummaryMetric ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:text-primary-600'}`}>
                                              <span className="truncate">{formatMetricLabel(key)}</span>
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="p-4">
                            <TimeSeriesChart
                              data={timeGrouping === 'weekly' ? aggregateToWeekly(summarySeries) : summarySeries}
                              metricKey={effectiveSummaryMetric}
                              multiSeries={platformIds.reduce((acc, pid) => { acc[pid] = [effectiveSummaryMetric]; return acc; }, {} as Record<string, string[]>)}
                              platformColors={platformColors}
                              showLegend
                              currentLegend={details?.title}
                              dateLocale={dateLocaleObj}
                            />
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="rounded-xl border border-black shadow-lg bg-white overflow-hidden h-full">
                      <div className="p-6 border-b border-gray-200" style={{ backgroundColor: "#F2F0FD" }}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-3">
                            <SocialIcon platform={selectedPlatform} size={20} className="mr-1" />
                            <h2 className="text-lg font-semibold text-gray-900 font-title">
                              {t('analytics.timeSeries.title', 'Daily evolution')}
                            </h2>
                          </div>
                          <div className="ml-auto flex items-center gap-3">
                            {/* Granularity toggle */}
                            <div className="inline-flex border border-black rounded-lg overflow-hidden">
                              <button
                                type="button"
                                onClick={() => setTimeGrouping('daily')}
                                className={`px-3 py-1 text-xs ${timeGrouping === 'daily' ? 'bg-black text-white' : 'bg-white text-gray-700'}`}
                              >
                                {t('analytics.timeSeries.daily', 'Daily')}
                              </button>
                              <button
                                type="button"
                                onClick={() => setTimeGrouping('weekly')}
                                className={`px-3 py-1 text-xs border-l border-black ${timeGrouping === 'weekly' ? 'bg-black text-white' : 'bg-white text-gray-700'}`}
                              >
                                {t('analytics.timeSeries.weekly', 'Weekly')}
                              </button>
                            </div>
                             {sortedMetricsForDropdown.length > 0 && (
                            <div className="relative min-w-60">
                              <button
                                type="button"
                                onClick={() => setIsMetricDropdownOpen(!isMetricDropdownOpen)}
                                className="w-full flex items-center justify-between gap-2 px-4 py-2 rounded-lg hover:text-primary-600 transition-colors bg-white border-[0.5px] border-black"
                              >
                                <span className="text-sm text-gray-700 truncate">
                                  {formatMetricLabel(effectiveMetricKey)}
                                </span>
                                <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                              </button>
                              {isMetricDropdownOpen && (
                                <>
                                  <div className="fixed inset-0 z-10" onClick={() => setIsMetricDropdownOpen(false)} />
                                  <div className="absolute left-0 right-0 mt-2 w-full rounded-lg bg-white shadow-lg z-20 border-[0.5px] border-black">
                                    <div className="py-1 max-h-[28rem] overflow-y-auto">
                                       {sortedMetricsForDropdown.map((key) => (
                                        <button
                                          type="button"
                                          key={key}
                                          onClick={() => { setSelectedTimeSeriesMetric(key); setIsMetricDropdownOpen(false); }}
                                          className={`w-full flex items-center gap-2 px-4 py-2 text-sm ${
                                            key === effectiveMetricKey ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:text-primary-600'
                                          }`}
                                        >
                                          <span className="truncate">{formatMetricLabel(key)}</span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="relative">
                          <TimeSeriesChart
                            data={(Array.isArray(timeSeriesData) ? (timeGrouping === 'weekly' ? aggregateToWeekly(timeSeriesData as any) : timeSeriesData) : []) as any}
                            previousData={(Array.isArray(previousTimeSeriesData) ? (timeGrouping === 'weekly' ? aggregateToWeekly(previousTimeSeriesData as any) : previousTimeSeriesData) : undefined) as any}
                            metricKey={effectiveMetricKey}
                            color="#6366f1"
                            previousColor="#a5b4fc"
                            dimmed={Array.isArray(timeSeriesData) ? isAllZeroForEffectiveMetric : true}
                            overlayText={t('analytics.noActivity', 'No activity for this period')}
                            showLegend
                            currentLegend={`${t('analytics.timeSeries.current', 'Current period')} · ${(() => { try { const s = new Date(formattedDateRange.start); const e = new Date(formattedDateRange.end); const f = (d: Date) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; return `${f(s)} - ${f(e)}`; } catch { return '' } })()}`}
                            previousLegend={formattedPreviousDateRange ? `${t('analytics.timeSeries.previous', 'Previous period')} · ${formattedPreviousDateRange}` : undefined}
                            currentTooltipLabel={t('analytics.timeSeries.currentShort', 'Current')}
                            previousTooltipLabel={t('analytics.timeSeries.previousShort', 'Previous')}
                            dateLocale={(
                              i18n.language?.startsWith('fr') ? fr :
                              i18n.language?.startsWith('de') ? de :
                              i18n.language?.startsWith('it') ? itLocale :
                              i18n.language?.startsWith('es') ? esLocale :
                              i18n.language?.startsWith('pt') ? ptBR : enUS
                            )}
                          />
                          {(!Array.isArray(timeSeriesData) || isAllZeroForEffectiveMetric) && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="bg-white/70 text-gray-700 px-4 py-2 rounded-lg border border-gray-300 text-sm">
                                {t('analytics.noActivity', 'No activity for this period')}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                </div>

                {/* Main Metrics */}
                <div className="space-y-4">
                  {(activeTab === 'summary' ? getSummaryMetrics() : 
                    ((platformData as any)?.keyMetrics || [] as BackendMetricItem[]).map((metric: BackendMetricItem) => {
                    const details = getMetricDisplayDetails(metric.type);
                    const change = parseChangePercentage(metric.variationsPercentage);
                      const displayValue = metric.type === 'AVERAGEVIEWDURATION' 
                        ? computeAverageFromTimeSeries('AVERAGEVIEWDURATION') 
                        : metric.total;
                    const hideFollowersUi = (
                      [
                        'FOLLOWERS_COUNT',
                        'TOTAL_FOLLOWERS_COUNT',
                        'FOLLOWERS',
                        'TOTAL_FOLLOWERS',
                        'FOLLOWER_COUNT',
                        'INSTAGRAM_FOLLOWERS',
                        'FACEBOOK_FOLLOWERS',
                        'LINKEDIN_FOLLOWERS'
                      ] as string[]
                    ).includes(metric.type);
                    return {
                      title: details.title,
                        value: displayValue,
                      previousValue: metric.previousTotal,
                      change: change,
                      icon: details.icon,
                      color: details.color,
                      rawValueChange: metric.variationsPercentage?.toString(),
                      isPercentage: ['ENGAGEMENT_RATE','AVG_ENGAGEMENT_RATE','CTR'].includes(metric.type),
                      description: (metric as any).description,
                      hideChange: hideFollowersUi,
                      hidePrevious: hideFollowersUi
                    } as DisplayMetric; // Ensure the returned object matches DisplayMetric
                  })).map((metric: DisplayMetric, index: number) => (
                    <div key={index} className="rounded-xl border border-black shadow-lg bg-white p-4">
                      <div className="flex items-center justify-between">
                        <div className={`${metric.color} bg-opacity-10 p-2 rounded-lg`}>
                          <metric.icon className={`w-5 h-5 ${metric.color.replace('bg-', 'text-')}`} />
                        </div>
                        {!metric.hideChange && (
                        <div className="flex items-center space-x-1">
                          <span className={`text-xs font-medium ${
                            metric.change > 0 ? 'text-green-600' : metric.change === 0 ? 'text-gray-600' : metric.change < 0 ? 'text-red-600' : 'text-gray-600' // Handle Infinity/NaN
                          }`}>
                            {isFinite(metric.change) ? 
                              `${metric.change > 0 ? '+' : ''}${metric.change.toFixed(1)}%` : 
                              (metric.rawValueChange || (metric.change > 0 ? '∞' : metric.change < 0 ? '-∞' : t('common.notAvailable', 'N/A')))
                            }
                          </span>
                          {isFinite(metric.change) && metric.change !== 0 ? (
                            metric.change > 0 ? (
                              <ArrowUpRight size={12} className="text-green-600" />
                            ) : (
                              <ArrowDownRight size={12} className="text-red-600" />
                            )
                          ) : metric.rawValueChange === "∞" || metric.change === Infinity ? (
                             <ArrowUpRight size={12} className="text-green-600" />
                          ) : metric.rawValueChange === "-∞" || metric.change === -Infinity ? (
                             <ArrowDownRight size={12} className="text-red-600" />
                          ) : metric.rawValueChange === "0" && metric.change === 0 ? null /* Explicitly no icon for 0% exact */ : 
                             (metric.rawValueChange || metric.change !== 0) ? null : /* No icon if change is NaN but not explicitly 0 */ null }
                        </div>
                        )}
                      </div>
                      <div className="mt-3">
                         <h3 className="text-base font-semibold text-gray-500 font-body flex items-center gap-1">
                           <span>{metric.title}</span>
                           {metric.description ? (
                             <InfoTooltip message={metric.description} position="top" />
                           ) : null}
                         </h3>
                        <p className="mt-1 text-2xl font-semibold text-gray-900 font-title">
                          {metric.isPercentage ? `${metric.value.toFixed(2)}%` : (
                            metric.title && (metric.title.includes('Rate') || metric.title.includes('CTR') || metric.title.includes('Avg. View Duration')) ? `${metric.value.toFixed(2)}${metric.title.includes('Rate') || metric.title.includes('CTR') ? '%' : ''}` : metric.value.toLocaleString()
                          )}
                        </p>
                        {!metric.hidePrevious && (
                          <p className="mt-1 text-xs text-gray-400">
                            {t('common.previous')}: {typeof metric.previousValue === 'number' ? metric.previousValue.toLocaleString() : t('common.notAvailable', 'N/A')}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Secondary Metrics */}
              {( 
                (activeTab === 'summary' && (analyticsSummary as any)?.secondaryMetrics && ((analyticsSummary as any).secondaryMetrics as BackendMetricItem[]).length > 0) ||
                (activeTab !== 'summary' && (platformData as any)?.secondaryMetrics && ((platformData as any).secondaryMetrics as BackendMetricItem[]).length > 0)
              ) && (
                <div className="space-y-6 pb-8">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {activeTab === 'summary' ? (
                        t('analytics.specificPlatformsMetricsTitle', 'Specific Platforms Metrics')
                      ) : (
                        <div className="flex items-center">
                          <SocialIcon platform={selectedPlatform} size={20} className="mr-2" />
                          {t('analytics.moreMetricsTitle', 'More {{platform}} Metrics', {
                            platform: platforms.find(p => p.id === activeTab)?.name || 'Platform'
                          })}
                        </div>
                      )}
                    </h2>
                  </div>
                  {activeTab === 'youtube' && (
                    <p className="-mt-4 text-sm text-gray-500">
                      {t(
                        'analytics.youtubeIngestionLag',
                        'Certaines données peuvent prendre jusqu\'à 48h pour nous être envoyées par YouTube.'
                      )}
                    </p>
                  )}

                  {(() => {
                    const forSummary = activeTab === 'summary';
                    const allSecondary = (forSummary
                      ? ((analyticsSummary as any).secondaryMetrics || [])
                      : ((platformData as any).secondaryMetrics || [])) as BackendMetricItem[];

                    // Branch: keep original per-platform grid for platform tabs
                    if (!forSummary) {
                      const sorted = [...allSecondary];
                      const seen = new Set<string>();
                      const dedup = sorted.filter((m) => {
                        const details = getMetricDisplayDetails(m.type);
                        const title = details.title || m.type;
                        const platformKey = (m as any).platform || '';
                        const key = `${title}::${platformKey}`;
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                      });
                      const visible = dedup.slice(0, secondaryVisibleCount);
                      const remaining = Math.max(dedup.length - secondaryVisibleCount, 0);
                      return (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {visible.map((m: BackendMetricItem, index: number) => {
                              const details = getMetricDisplayDetails(m.type);
                              const change = parseChangePercentage(m.variationsPercentage);
                              const color = details.color;
                              const Icon = details.icon as React.ElementType;
                              const title = details.title;
                              const previousValue = m.previousTotal;
                              const rawValueChange = (m as any).variationsPercentage?.toString();
                              return (
                                <div key={`${m.type}-${index}`} className="rounded-xl p-4 border border-black shadow-lg hover:shadow-xl transition-shadow bg-white">
                                  <div className="flex items-center justify-between">
                                    <div className={`${color} bg-opacity-10 p-2 rounded-lg`}>
                                      {'platform' in (m as any) && (m as any).platform ? (
                                        <SocialIcon platform={(m as any).platform as SocialPlatform} size={20} />
                                      ) : (
                                        <Icon className={`w-5 h-5 ${color.replace('bg-', 'text-')}`} />
                                      )}
                                    </div>
                                    <div className="flex items-center space-x-1">
                                      <span className={`text-xs font-medium ${change > 0 ? 'text-green-600' : change === 0 ? 'text-gray-600' : change < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                        {isFinite(change) ? `${change > 0 ? '+' : ''}${change.toFixed(1)}%` : (rawValueChange || (change > 0 ? '∞' : change < 0 ? '-∞' : t('common.notAvailable', 'N/A')))}
                                      </span>
                                      {isFinite(change) && change !== 0 ? (
                                        change > 0 ? (
                                          <ArrowUpRight size={12} className="text-green-600" />
                                        ) : (
                                          <ArrowDownRight size={12} className="text-red-600" />
                                        )
                                      ) : rawValueChange === '∞' || change === Infinity ? (
                                        <ArrowUpRight size={12} className="text-green-600" />
                                      ) : rawValueChange === '-∞' || change === -Infinity ? (
                                        <ArrowDownRight size={12} className="text-red-600" />
                                      ) : null}
                                    </div>
                                  </div>
                                  <div className="mt-3">
                                       <h3 className="text-base font-semibold text-gray-500 font-body flex items-center gap-1">
                                         <span>{title}</span>
                                         {m.description ? (
                                           <InfoTooltip message={m.description} position="top" />
                                         ) : null}
                                       </h3>
                                    <p className="mt-1 text-2xl font-semibold text-gray-900 font-title">
                                      {title && (title.includes('Rate') || title.includes('CTR') || title.includes('Avg. View Duration')) ? `${(
                                        m.type === 'AVERAGEVIEWDURATION' ? computeAverageFromTimeSeries('AVERAGEVIEWDURATION') : m.total
                                      ).toFixed(2)}${title.includes('Rate') || title.includes('CTR') ? '%' : ''}` : (
                                        m.type === 'AVERAGEVIEWDURATION' ? Math.round(computeAverageFromTimeSeries('AVERAGEVIEWDURATION')).toLocaleString() : m.total.toLocaleString()
                                      )}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-400">
                                      {t('common.previous')}: {typeof previousValue === 'number' ? previousValue.toLocaleString() : t('common.notAvailable', 'N/A')}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {remaining > 0 && (
                            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${showAllSecondaryMetrics ? 'max-h-[4000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                                {dedup.slice(secondaryVisibleCount).map((m: BackendMetricItem, index: number) => {
                                  const details = getMetricDisplayDetails(m.type);
                                  const change = parseChangePercentage(m.variationsPercentage);
                                  const color = details.color;
                                  const Icon = details.icon as React.ElementType;
                                  const title = details.title;
                                  const previousValue = m.previousTotal;
                                  const rawValueChange = (m as any).variationsPercentage?.toString();
                                  const valueForDisplay = m.type === 'AVERAGEVIEWDURATION' 
                                    ? computeAverageFromTimeSeries('AVERAGEVIEWDURATION') 
                                    : m.total;
                                  return (
                                    <div key={`${m.type}-more-${index}`} className="rounded-xl p-4 border border-black shadow-lg hover:shadow-xl transition-shadow bg-white">
                                  <div className="flex items-center justify-between">
                                    <div className={`${color} bg-opacity-10 p-2 rounded-lg`}>
                                      {'platform' in (m as any) && (m as any).platform ? (
                                        <SocialIcon platform={(m as any).platform as SocialPlatform} size={20} />
                                      ) : (
                                        <Icon className={`w-5 h-5 ${color.replace('bg-', 'text-')}`} />
                                      )}
                                    </div>
                                        <div className="flex items-center space-x-1">
                                          <span className={`text-xs font-medium ${change > 0 ? 'text-green-600' : change === 0 ? 'text-gray-600' : change < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                            {isFinite(change) ? `${change > 0 ? '+' : ''}${change.toFixed(1)}%` : (rawValueChange || (change > 0 ? '∞' : change < 0 ? '-∞' : t('common.notAvailable', 'N/A')))}
                                          </span>
                                          {isFinite(change) && change !== 0 ? (
                                            change > 0 ? (
                                              <ArrowUpRight size={12} className="text-green-600" />
                                            ) : (
                                              <ArrowDownRight size={12} className="text-red-600" />
                                            )
                                          ) : rawValueChange === '∞' || change === Infinity ? (
                                            <ArrowUpRight size={12} className="text-green-600" />
                                          ) : rawValueChange === '-∞' || change === -Infinity ? (
                                            <ArrowDownRight size={12} className="text-red-600" />
                                          ) : null}
                                        </div>
                                      </div>
                                  <div className="mt-3">
                                    <h3 className="text-base font-semibold text-gray-500 font-body flex items-center gap-1">
                                      <span>{title}</span>
                                      {m.description ? (
                                        <InfoTooltip message={m.description} position="top" />
                                      ) : null}
                                    </h3>
                                        <p className="mt-1 text-2xl font-semibold text-gray-900 font-title">
                                        {title && (title.includes('Rate') || title.includes('CTR') || title.includes('Avg. View Duration')) ? `${(
                                            valueForDisplay
                                          ).toFixed(2)}${title.includes('Rate') || title.includes('CTR') ? '%' : ''}` : (
                                            m.type === 'AVERAGEVIEWDURATION' ? Math.round(valueForDisplay).toLocaleString() : valueForDisplay.toLocaleString()
                                          )}
                                        </p>
                                        <p className="mt-1 text-xs text-gray-400">
                                          {t('common.previous')}: {typeof previousValue === 'number' ? previousValue.toLocaleString() : t('common.notAvailable', 'N/A')}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {remaining > 0 && (
                            <div className="mt-4 flex justify-center">
                              <button
                                type="button"
                                onClick={() => setShowAllSecondaryMetrics(!showAllSecondaryMetrics)}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-primary-600 text-primary-700 rounded-full shadow-sm hover:bg-primary-50 transition-colors"
                              >
                                <span className="text-sm font-medium">
                                  {showAllSecondaryMetrics
                                    ? t('analytics.secondaryMetrics.hide', 'Hide additional metrics')
                                    : t('analytics.secondaryMetrics.seeMore', 'See the {{count}} other metrics', { count: remaining })}
                                </span>
                                <ChevronDown className={`w-4 h-4 transition-transform ${showAllSecondaryMetrics ? 'rotate-180' : ''}`} />
                              </button>
                            </div>
                          )}
                        </>
                      );
                    }

                    // Summary: group followers and total posts in distinct rows
                    const isFollowerMetric = (type: string) => /_FOLLOWERS$/.test(type) || /SUBSCRIBERS$/.test(type);
                    const isTotalPostsMetric = (type: string) => type === 'TOTAL_POSTS' || type === 'POSTS';
                    const dedupPerPlatform = (arr: BackendMetricItem[]) => {
                      const seen = new Set<string>();
                      return arr.filter((m) => {
                        const details = getMetricDisplayDetails(m.type);
                        const title = details.title || m.type;
                        const platformKey = (m as any).platform || '';
                        const key = `${title}::${platformKey}`;
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                      });
                    };
                    const followerRow = dedupPerPlatform(allSecondary.filter((m) => isFollowerMetric(m.type)));
                    const postsRow = dedupPerPlatform(allSecondary.filter((m) => isTotalPostsMetric(m.type)));
                    const othersRaw = allSecondary.filter((m) => !isFollowerMetric(m.type) && !isTotalPostsMetric(m.type));
                    const othersDedup = dedupPerPlatform(othersRaw);

                    const renderCard = (m: BackendMetricItem, index: number, keySuffix = '') => {
                      const details = getMetricDisplayDetails(m.type);
                      const change = parseChangePercentage(m.variationsPercentage);
                      const color = details.color;
                      const Icon = details.icon as React.ElementType;
                      const title = details.title;
                      const previousValue = m.previousTotal;
                      const rawValueChange = (m as any).variationsPercentage?.toString();
                      const valueForDisplay = m.type === 'AVERAGEVIEWDURATION' 
                        ? computeAverageFromTimeSeries('AVERAGEVIEWDURATION') 
                        : m.total;
                      return (
                        <div key={`${m.type}-${keySuffix}-${index}`} className="rounded-xl p-4 border border-black shadow-lg hover:shadow-xl transition-shadow bg-white">
                          <div className="flex items-center justify-between">
                            <div className={`${color} bg-opacity-10 p-2 rounded-lg`}>
                              {'platform' in (m as any) && (m as any).platform ? (
                                <SocialIcon platform={(m as any).platform as SocialPlatform} size={20} />
                              ) : (
                                <Icon className={`w-5 h-5 ${color.replace('bg-', 'text-')}`} />
                              )}
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className={`text-xs font-medium ${change > 0 ? 'text-green-600' : change === 0 ? 'text-gray-600' : change < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                {isFinite(change) ? `${change > 0 ? '+' : ''}${change.toFixed(1)}%` : (rawValueChange || (change > 0 ? '∞' : change < 0 ? '-∞' : t('common.notAvailable', 'N/A')))}
                              </span>
                              {isFinite(change) && change !== 0 ? (
                                change > 0 ? (
                                  <ArrowUpRight size={12} className="text-green-600" />
                                ) : (
                                  <ArrowDownRight size={12} className="text-red-600" />
                                )
                              ) : rawValueChange === '∞' || change === Infinity ? (
                                <ArrowUpRight size={12} className="text-green-600" />
                              ) : rawValueChange === '-∞' || change === -Infinity ? (
                                <ArrowDownRight size={12} className="text-red-600" />
                              ) : null}
                            </div>
                          </div>
                          <div className="mt-3">
                            <h3 className="text-base font-semibold text-gray-500 font-body flex items-center gap-1">
                              <span>{title}</span>
                              {m.description ? (
                                <InfoTooltip message={m.description} position="top" />
                              ) : null}
                            </h3>
                            <p className="mt-1 text-2xl font-semibold text-gray-900 font-title">
                              {title && (title.includes('Rate') || title.includes('CTR') || title.includes('Avg. View Duration')) ? `${(
                                valueForDisplay
                              ).toFixed(2)}${title.includes('Rate') || title.includes('CTR') ? '%' : ''}` : (
                                m.type === 'AVERAGEVIEWDURATION' ? Math.round(valueForDisplay).toLocaleString() : valueForDisplay.toLocaleString()
                              )}
                            </p>
                            <p className="mt-1 text-xs text-gray-400">
                              {t('common.previous')}: {typeof previousValue === 'number' ? previousValue.toLocaleString() : t('common.notAvailable', 'N/A')}
                            </p>
                          </div>
                        </div>
                      );
                    };

                    const visibleOthers = othersDedup.slice(0, secondaryVisibleCount);
                    const remaining = Math.max(othersDedup.length - secondaryVisibleCount, 0);
                    return (
                      <>
                        {followerRow.length > 0 && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {followerRow.map((m, i) => renderCard(m, i, 'followers'))}
                          </div>
                        )}
                        {postsRow.length > 0 && (
                          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {postsRow.map((m, i) => renderCard(m, i, 'posts'))}
                          </div>
                        )}
                        {visibleOthers.length > 0 && (
                          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {visibleOthers.map((m, i) => renderCard(m, i, 'others'))}
                          </div>
                        )}
                        {remaining > 0 && (
                          <div className={`overflow-hidden transition-all duration-500 ease-in-out ${showAllSecondaryMetrics ? 'max-h-[4000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                              {othersDedup.slice(secondaryVisibleCount).map((m, i) => renderCard(m, i, 'others-more'))}
                            </div>
                          </div>
                        )}
                        {remaining > 0 && (
                          <div className="mt-4 flex justify-center">
                            <button
                              type="button"
                              onClick={() => setShowAllSecondaryMetrics(!showAllSecondaryMetrics)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-primary-600 text-primary-700 rounded-full shadow-sm hover:bg-primary-50 transition-colors"
                            >
                              <span className="text-sm font-medium">
                                {showAllSecondaryMetrics
                                  ? t('analytics.secondaryMetrics.hide', 'Hide additional metrics')
                                  : t('analytics.secondaryMetrics.seeMore', 'See the {{count}} other metrics', { count: remaining })}
                              </span>
                              <ChevronDown className={`w-4 h-4 transition-transform ${showAllSecondaryMetrics ? 'rotate-180' : ''}`} />
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Your top posts - section (supports summary and platform) */}
              {Array.isArray(topPosts) && (
                <div className="space-y-4 pb-8 mt-6">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold text-gray-900">
                      <div className="flex items-center">
                        {activeTab !== 'summary' && (
                          <SocialIcon platform={selectedPlatform} size={20} className="mr-2" />
                        )}
                        {t('analytics.topPosts.title', '🏆 Your top posts from the last 30 days')}
                      </div>
                    </h2>
                  </div>
                  {topPosts.length === 0 ? (
                    <div className="text-sm text-gray-500 border border-gray-200 rounded-lg p-4 bg-white">
                      {t('analytics.topPosts.none', 'No posts have been published during this period')}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {topPosts.map((post: any, idx: number) => {
                      const thumb = post?.content?.thumbnail_url || post?.content?.media_url || '';
                      const text: string = post?.content?.text || '';
                      const snippet = text.length > 110 ? `${text.slice(0, 110)}…` : text;
                      const createdAt = post?.created_at ? new Date(post.created_at) : null;
                      const dateStr = createdAt && !isNaN(createdAt.getTime()) ? format(createdAt, 'MMM d, yyyy') : '';
                      const permalink = post?.content?.permalink;
                      const metrics = post?.metrics || {};
                      const postPlatform = (post?.platform || (activeTab !== 'summary' ? selectedPlatform : undefined)) as SocialPlatform | undefined;
                      const prettyPlatformName = postPlatform ? ({
                        facebook: 'Facebook',
                        instagram: 'Instagram',
                        linkedin: 'LinkedIn',
                        youtube: 'YouTube',
                        tiktok: 'TikTok',
                        twitter: 'Twitter',
                        pinterest: 'Pinterest',
                        'google-business': 'Google Business'
                      } as Record<string, string>)[postPlatform] || (postPlatform.charAt(0).toUpperCase() + postPlatform.slice(1)) : '';
                      const { emoji: mediaEmoji, label: mediaLabel } = getMediaEmojiAndLabelFromPost(post);
                      const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '';
                      return (
                        <div key={post.id} className="flex gap-3 p-3 border border-black rounded-lg bg-white">
                          <div className="relative w-20 h-20 flex-shrink-0">
                            <div className="w-full h-full border border-black overflow-hidden rounded-md bg-gray-50">
                              {thumb ? (
                                <img src={thumb} alt="post" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">No image</div>
                              )}
                            </div>
                            {medal && (
                              <div className="absolute -top-2 -right-2 w-9 h-9 bg-white border border-black rounded-md flex items-center justify-center shadow-sm z-10">
                                <span className="text-2xl leading-none" aria-hidden="true">{medal}</span>
                              </div>
                            )}
                          </div>
                              <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {snippet || t('analytics.topPosts.noText', 'No text')}
                              </p>
                              {permalink && (
                                <a
                                  href={permalink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-indigo-600 hover:underline flex-shrink-0"
                                >
                                  {t('analytics.topPosts.view', 'Open')}
                                </a>
                              )}
                            </div>
                                <div className="mt-1 text-xs text-gray-500 flex items-center gap-2">
                                  <span>{dateStr}</span>
                                  {postPlatform && (
                                    <span className="group relative inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-white border border-gray-300 text-gray-700">
                                      <SocialIcon platform={postPlatform} size={12} />
                                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                        {prettyPlatformName}
                                      </div>
                                    </span>
                                  )}
                                  <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-primary-50 border border-primary-200 text-primary-700">
                                    {mediaEmoji} {mediaLabel}
                                  </span>
                                </div>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              {'likes' in metrics && (
                                <span className="group relative inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-black bg-white">
                                  <span className="text-[13px]">❤️</span> <strong className="font-semibold" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>{metrics.likes}</strong>
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                    {t('analytics.metrics.likes','Likes')}
                                  </div>
                                </span>
                              )}
                              {'comments' in metrics && (
                                <span className="group relative inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-black bg-white">
                                  <span className="text-[13px]">💬</span> <strong className="font-semibold" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>{metrics.comments}</strong>
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                    {t('analytics.metrics.comments','Comments')}
                                  </div>
                                </span>
                              )}
                              {'shares' in metrics && (
                                <span className="group relative inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-black bg-white">
                                  <span className="text-[13px]">🔗</span> <strong className="font-semibold" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>{metrics.shares}</strong>
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                    {t('analytics.metrics.shares','Shares')}
                                  </div>
                                </span>
                              )}
                              {'clicks' in metrics && (
                                <span className="group relative inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-black bg-white">
                                  <span className="text-[13px]">👉</span> <strong className="font-semibold" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>{metrics.clicks}</strong>
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                    {t(
                                      postPlatform === 'facebook' ? 'analytics.metrics.pageClicks' : 'analytics.metrics.clicks',
                                      postPlatform === 'facebook' ? 'Page Clicks' : 'Clicks'
                                    )}
                                  </div>
                                </span>
                              )}
                              {'views' in metrics && (
                                <span className="group relative inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-black bg-white">
                                  <span className="text-[13px]">👁️</span> <strong className="font-semibold" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>{metrics.views}</strong>
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                    {t('analytics.metrics.views','Views')}
                                  </div>
                                </span>
                              )}
                              {'unique_views' in metrics && (
                                <span className="group relative inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-black bg-white">
                                  <span className="text-[13px]">🔍</span> <strong className="font-semibold" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>{metrics.unique_views}</strong>
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                    {t('analytics.metrics.uniqueViews','Unique Views')}
                                  </div>
                                </span>
                              )}
                              {'reactions' in metrics && (
                                <span className="group relative inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-black bg-white">
                                  <span className="text-[13px]">☺️</span> <strong className="font-semibold" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>{metrics.reactions}</strong>
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-primary-100 text-gray-900 text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                    {t('analytics.metrics.reactions','Reactions')}
                                  </div>
                                </span>
                              )}
                              {'engagement_rate' in metrics && (
                                <span className="group relative inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-black bg-white">
                                  <span className="text-[13px]">🔥</span> <strong className="font-semibold" style={{ fontFamily: 'Bricolage Grotesque, ui-sans-serif, system-ui' }}>{`${Math.round(typeof metrics.engagement_rate === 'number' ? metrics.engagement_rate : Number(metrics.engagement_rate || 0))}%`}</strong>
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-[10px] rounded shadow opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-20">
                                    {t('analytics.metrics.engagementRate','Engagement Rate')}
                                  </div>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  )}
                </div>
              )}
              {/* Third Metrics - New Section */}
              {activeTab === 'summary' && (analyticsSummary as any)?.thirdMetrics && ((analyticsSummary as any).thirdMetrics as BackendMetricItem[]).length > 0 && (
                <div className="space-y-6 pb-8">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {t('analytics.thirdMetricsSummaryTitle', 'Other relevant metrics')}
                    </h2>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    { ((analyticsSummary as any).thirdMetrics || [] as BackendMetricItem[])
                      .map((metric: BackendMetricItem) => {
                        // Process metric for display (similar to secondary metrics)
                        const details = getMetricDisplayDetails(metric.type);
                        const change = parseChangePercentage(metric.variationsPercentage);
                        return {
                          ...metric, // Spread original metric properties
                          title: details.title,
                          value: metric.total,
                          previousValue: metric.previousTotal,
                          change: change,
                          icon: details.icon, // Fallback icon
                          color: details.color,
                          rawValueChange: metric.variationsPercentage?.toString(),
                          description: (metric as any).description
                        } as DisplayMetric & { platform?: string };
                      })
                      .map((metric: DisplayMetric & { platform?: string }, index: number) => (
                        <div 
                          key={index} 
                          className="rounded-xl p-4 border border-black shadow-lg hover:shadow-xl transition-shadow bg-white"
                        >
                          <div className="flex items-center justify-between">
                            <div className={`${metric.color} bg-opacity-10 p-2 rounded-lg`}>
                              {metric.platform ? (
                                <SocialIcon platform={metric.platform as SocialPlatform} size={20} />
                              ) : (
                                <metric.icon className={`w-5 h-5 ${metric.color.replace('bg-', 'text-')}`} />
                              )}
                            </div>
                            <div className="flex items-center space-x-1">
                               <span className={`text-xs font-medium ${metric.change > 0 ? 'text-green-600' : metric.change === 0 ? 'text-gray-600' : metric.change < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                {isFinite(metric.change) ? 
                                  `${metric.change > 0 ? '+' : ''}${metric.change.toFixed(1)}%` : 
                                  (metric.rawValueChange || (metric.change > 0 ? '∞' : metric.change < 0 ? '-∞' : t('common.notAvailable', 'N/A')))}
                              </span>
                              {isFinite(metric.change) && metric.change !== 0 ? (
                                metric.change > 0 ? (
                                  <ArrowUpRight size={12} className="text-green-600" />
                                ) : (
                                  <ArrowDownRight size={12} className="text-red-600" />
                                )
                              ) : metric.rawValueChange === "∞" || metric.change === Infinity ? (
                                 <ArrowUpRight size={12} className="text-green-600" />
                              ) : metric.rawValueChange === "-∞" || metric.change === -Infinity ? (
                                 <ArrowDownRight size={12} className="text-red-600" />
                              ) : metric.rawValueChange === "0" && metric.change === 0 ? null 
                                 : (metric.rawValueChange || metric.change !== 0) ? null : null }
                            </div>
                          </div>
                          <div className="mt-3">
                            <h3 className="text-base font-semibold text-gray-500 font-body flex items-center gap-1">
                              <span>{metric.title}</span>
                              {metric.description ? (
                                <InfoTooltip message={metric.description} position="top" />
                              ) : null}
                            </h3>
                            <p className="mt-1 text-2xl font-semibold text-gray-900 font-title">
                               {metric.title && (metric.title.includes('Rate') || metric.title.includes('CTR') || metric.title.includes('Avg. View Duration')) ? `${metric.value.toFixed(2)}${metric.title.includes('Rate') || metric.title.includes('CTR') ? '%' : ''}` : metric.value.toLocaleString()}
                            </p>
                            <p className="mt-1 text-xs text-gray-400">
                              {t('common.previous')}: {typeof metric.previousValue === 'number' ? metric.previousValue.toLocaleString() : t('common.notAvailable', 'N/A')}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </>
          )}
    </PageLayout>
  );
};

export default Analytics;